// hooks/useFiltersQuery.ts
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

export interface Filters {
  page: number;
  size: number;
  sort?: string;
  keyword?: string;
  type?: string[];
  status?: string[];
  category?: number[];
  companies?: number[];
  excludeCompanies?: boolean;
  person?: number;
  region?: number;
  initiator?: string;
  startedAfter?: string;
  startedBefore?: string;
  expiredAfter?: string;
  expiredBefore?: string;
  committedAfter?: string;
  committedBefore?: string;
  tab?: string;
}

const ARRAY_KEYS = ['type', 'status', 'category', 'companies'] as const;
const NUMBER_KEYS = ['page', 'size', 'person', 'region'] as const;
const BOOLEAN_KEYS = ['excludeCompanies'] as const;
const STRING_KEYS = [
  'sort',
  'keyword',
  'initiator',
  'startedAfter',
  'startedBefore',
  'expiredAfter',
  'expiredBefore',
  'committedAfter',
  'committedBefore',
  'tab',
] as const;

type SetFiltersOptions = { replace?: boolean };

export function useFiltersQuery(
  defaults: Partial<Filters> = { page: 0, size: 10 },
) {
  const { query, pathname, push } = useRouter();

  const searchFilters = useMemo<Filters>(() => {
    const out: any = { ...defaults };

    for (const key of NUMBER_KEYS) {
      const v = query[key];
      if (typeof v === 'string' && !isNaN(Number(v))) out[key] = Number(v);
    }
    for (const key of BOOLEAN_KEYS) {
      if (query[key] === 'true') out[key] = true;
      else if (query[key] === 'false') out[key] = false;
    }
    for (const key of STRING_KEYS) {
      const v = query[key];
      if (typeof v === 'string') out[key] = v;
    }
    for (const key of ARRAY_KEYS) {
      const raw = query[key];
      if (typeof raw === 'string' && raw) {
        const parts = raw.split(',').filter((s) => s);
        out[key] =
          key === 'category' || key === 'companies' ? parts.map(Number) : parts;
      }
    }
    return out;
  }, [query, defaults]);

  const setSearchFilters = useCallback(
    (next: Partial<Filters>, opts?: SetFiltersOptions) => {
      const base: Partial<Filters> = opts?.replace ? {} : searchFilters;
      const merged = { ...base, ...next };

      const page = merged.page ?? defaults.page ?? 0;
      const size = merged.size ?? defaults.size ?? 10;

      const sort = merged.sort ?? defaults.sort;

      const qp: Record<string, string> = {};
      qp.page = String(page);
      qp.size = String(size);
      if (sort) qp.sort = String(sort);

      (NUMBER_KEYS as readonly string[])
        .concat(BOOLEAN_KEYS, STRING_KEYS)
        .forEach((key) => {
          const val = (merged as any)[key];
          if (
            val !== undefined &&
            val !== null &&
            !(typeof val === 'string' && val.trim() === '') &&
            key !== 'page' &&
            key !== 'size' &&
            key !== 'sort'
          ) {
            qp[key] = String(val);
          }
        });

      (ARRAY_KEYS as readonly string[]).forEach((key) => {
        const arr = (merged as any)[key];
        if (Array.isArray(arr) && arr.length > 0) {
          qp[key] = arr.join(',');
        }
      });

      push({ pathname, query: qp }, undefined, { shallow: true });
    },
    [searchFilters, defaults, pathname, push],
  );

  return [searchFilters, setSearchFilters] as const;
}
